import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TranscriptPanel, formatOffset } from './TranscriptPanel';
import { useModalDismiss } from './use-modal-dismiss';
import type { Transcript } from './api';
import { formatDuration, formatEventTime } from './format';

// One call's diarized transcript, in an overlay rather than inline: a long transcript
// has to scroll on its own instead of pushing the list it came from around.
//
// The header names the call — contact, SDR, when, how long — because the diarizer only
// labels anonymous "Speaker N" turns, so nothing inside the transcript says whose
// conversation this is. Fetch state is passed in, not owned here: the transcript is
// cached across opens, which it could not be if this component fetched it.
export function TranscriptModal({
  contact,
  sdr,
  at,
  durationSec,
  transcript,
  loading,
  error,
  onRetry,
  onClose,
}: {
  contact: string;
  sdr: string;
  at: string;
  durationSec: number | null;
  transcript: Transcript | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onClose: () => void;
}) {
  const dismiss = useModalDismiss(onClose);

  // Copies the conversation as plain text, one "Speaker [offset]: text" line per turn —
  // the same shape the panel renders, so what you paste matches what you read.
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(copyTimer.current), []);
  const copyTranscript = useCallback(() => {
    const segments = transcript?.conversation ?? [];
    if (segments.length === 0) return;
    const text = segments
      .map((seg) => `${seg.speaker} [${formatOffset(seg.startSec)}]: ${seg.text}`)
      .join('\n');
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 1500);
    });
  }, [transcript]);

  return (
    <div
      data-overlay-above
      // z-[60] (above the panel this launches from) so it reliably sits on top
      // regardless of portal insertion order.
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        // Stop here so a scrim click closes only THIS modal, never anything underneath
        // (React events bubble the component tree, not the DOM, across portals).
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        {...dismiss}
        className="bg-card flex max-h-[85vh] w-[min(44rem,92vw)] flex-col gap-3 overflow-hidden rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        aria-label={`Transcript of the call with ${contact}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold">
              Transcript · <span className="font-medium">{contact}</span>
            </h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {/* `sdr` already carries its own unassigned fallback, so it stands alone
                  here with no literal prefix. */}
              <span className="text-foreground font-medium">{sdr}</span>
              {' · '}
              <span className="font-mono tabular-nums">{formatEventTime(at)}</span>
              {durationSec != null && durationSec > 0 && (
                <>
                  {' · '}
                  <span className="tabular-nums">{formatDuration(durationSec)}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex flex-none items-center gap-2">
            {(transcript?.conversation?.length ?? 0) > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={copyTranscript}
                title="Copy the full transcript to the clipboard"
              >
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copied' : 'Copy transcript'}
              </Button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground flex-none text-lg"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error ? (
            <p className="text-muted-foreground inline-flex items-center gap-2 py-1 text-xs italic">
              {error}
              <Button variant="outline" size="sm" onClick={onRetry} disabled={loading}>
                Retry
              </Button>
            </p>
          ) : transcript ? (
            <TranscriptPanel transcript={transcript} />
          ) : (
            <p className="text-muted-foreground py-1 text-xs italic">Loading transcript…</p>
          )}
        </div>
      </div>
    </div>
  );
}
